import * as React from 'react';
// @ts-expect-error
import {useForm} from 'react-hook-form';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {useMutation} from '@apollo/react-hooks';
import _ from 'underscore';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {ConfirmationDialog} from 'shells/confirmation-dialog/confirmation-dialog';
import {FullPageEditor} from 'shells/layout/full-page-editor';
import {useDidMountEffect} from 'shells/utils/hooks/use-did-mount-effect';

import type {
    DiscardForm as DiscardFormMutation,
    DiscardFormVariables as DiscardFormMutationVariables,
    FormFragment,
    FormPublishInput,
    FormUpdateInput,
} from 'nutshell-graphql-types';

import FORM_DISCARD from '../graphql/mutations/discard-form.graphql';
import {
    convertGraphQLResponseToFormState,
    convertFormStateToGraphQL,
    useFormUpdate,
} from '../helpers';
import type {Form} from '../types';

import {FormEditorMainContent} from './editor/form-editor-main-content';
import {PublishModal} from './publish-modal/publish-modal';

type OwnProps = ContextRouter & {
    onPublishForm: (arg1: FormPublishInput) => Promise<any>;
    onClose: () => void;
    form: FormFragment;
    formHeader?: React.ReactNode;
    refetchForm: () => Promise<any>;
    disableDesign?: boolean;
    disableHeaderFields?: boolean;
    minimumOneEmailField?: boolean;
    isMeetingForm?: boolean;
};

type Props = OwnProps & {
    formDefaultValues: Form;
    onUpdateForm: (arg1: FormUpdateInput) => Promise<any>;
    onDiscardForm: (arg1: string) => Promise<any>;
    saveFailedErrorMessage: string | null | undefined;
    onClearError: () => void;
};

function FullPageFormEditorComponent(props: Props) {
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [isConfirmingDiscard, setIsConfirmingDiscard] = React.useState<boolean>(false);
    const [shouldShowPublishChangesModal, setShouldShowPublishChangesModal] =
        React.useState<boolean>(false);

    const {handleSubmit, control, watch} = useForm({
        defaultValues: props.formDefaultValues,
        mode: 'all',
        reValidateMode: 'all',
        delayError: 400,
    });

    const submitForm = React.useMemo(
        () =>
            _.debounce(
                // @ts-expect-error
                handleSubmit((formValues) => {
                    props.onUpdateForm(convertFormStateToGraphQL(formValues));
                }),
                500
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const formValues = JSON.stringify(watch());

    useDidMountEffect(() => {
        submitForm();
    }, [formValues, submitForm]);

    return (
        <>
            <FullPageEditor
                title={props.form.name}
                onBack={() => {
                    if (props.form.hasUnpublishedChanges) {
                        setIsConfirmingDiscard(true);
                    } else {
                        props.onClose();
                    }
                }}
                backButtonText={props.form.hasUnpublishedChanges ? 'Discard changes' : 'Back'}
                previewPath={
                    props.isMeetingForm ? undefined : `/preview/forms/${props.form.publicFormId}`
                }
                previewButtonText={props.isMeetingForm ? undefined : 'Preview form'}
                onSaveClick={() => {
                    if (props.form.isLiveForm) {
                        setShouldShowPublishChangesModal(true);
                    } else {
                        setIsSaving(true);
                        props.onPublishForm({form: {id: props.form.id}}).then(() => {
                            setIsSaving(false);
                            props.onClose();
                        });
                    }
                }}
                saveButtonText={props.form.isLiveForm ? 'Publish changes' : 'Save changes'}
                isSaving={isSaving}
                content={
                    <FormEditorMainContent
                        formId={props.form.id}
                        formHeader={props.formHeader}
                        control={control}
                        watch={watch}
                        postAddField={props.refetchForm}
                        saveFailedErrorMessage={props.saveFailedErrorMessage}
                        onClearError={props.onClearError}
                        disableDesign={props.disableDesign}
                        disableHeaderFields={props.disableHeaderFields}
                        minimumOneEmailField={props.minimumOneEmailField}
                        isMeetingForm={props.isMeetingForm}
                    />
                }
            />
            {isConfirmingDiscard ? (
                <ConfirmationDialog
                    title='Discard changes'
                    message='If you leave now, your changes will be discarded. Would you like to go back?'
                    confirmText='Yes, discard changes'
                    cancelText='No, stay here'
                    onCancel={() => {
                        setIsConfirmingDiscard(false);
                    }}
                    onConfirm={() => {
                        props.onDiscardForm(props.form.id).then(() => {
                            props.onClose();
                        });
                    }}
                    isConfirmDestructive={true}
                />
            ) : undefined}
            {shouldShowPublishChangesModal ? (
                <PublishModal
                    formId={props.form.id}
                    isMeetingForm={props.isMeetingForm}
                    onClose={() => {
                        setShouldShowPublishChangesModal(false);
                    }}
                    onPublish={() => {
                        props.onPublishForm({form: {id: props.form.id}}).then(() => {
                            setShouldShowPublishChangesModal(false);
                            props.onClose();
                        });
                    }}
                />
            ) : undefined}
        </>
    );
}

export function FullPageFormEditor(props: OwnProps) {
    const [saveFailedErrorMessage, setSaveFailedErrorMessage] = React.useState<
        string | null | undefined
    >(null);
    const updateForm = useFormUpdate();
    const [discardForm] = useMutation<DiscardFormMutation, DiscardFormMutationVariables>(
        FORM_DISCARD
    );

    return (
        <FullPageFormEditorComponent
            {...props}
            formDefaultValues={convertGraphQLResponseToFormState({
                ...props.form.editableVersion,
                id: props.form.id,
            })}
            onUpdateForm={(input: FormUpdateInput) => {
                return updateForm(input).catch((error) => {
                    const errorMessage = safelyGetGraphQLErrorMessage(error);
                    setSaveFailedErrorMessage(errorMessage);
                });
            }}
            onDiscardForm={(formId: string) => {
                return discardForm({variables: {input: {formId}}});
            }}
            saveFailedErrorMessage={saveFailedErrorMessage}
            onClearError={() => setSaveFailedErrorMessage(null)}
        />
    );
}
